import { useCallback, useEffect, useRef, useState } from 'react';
import useClient from '@pkg/client/useClient';
import useDatabase from '@pkg/database/useDatabase';

const GET_ACTIVITIES = /* GraphQL */ `
  query GetActivities {
    me {
      active_organisation {
        activities {
          uuid
          description
          order
          process_id
        }
      }
    }
  }
`;

// Coalesce refresh requests and back off failures within each hook instance.
export default function useRefresh() {
  const client = useClient();
  const database = useDatabase();
  const [isRefreshing, setRefreshing] = useState(false);
  const requestRefresh = useRef(null);
  const refresh = useCallback(() => requestRefresh.current?.(), []);

  useEffect(() => {
    setRefreshing(false);
    let active = true;
    let running = false;
    let pending = false;
    let timer = null;
    let failures = 0;

    async function run() {
      running = true;
      setRefreshing(true);
      try {
        while (active && pending) {
          pending = false;
          try {
            const response = await client.graphql({ query: GET_ACTIVITIES });
            if (!active) return;
            const organisation = response?.data?.me?.active_organisation;
            if (response?.errors?.length || !organisation) {
              throw new Error('Library activities response was incomplete');
            }

            const newActivities = organisation.activities ?? [];
            const ids = new Set(newActivities.map(({ uuid }) => uuid));
            // Publish the rows and freshness marker together. Events received
            // during this request schedule another pass after this write.
            await database.transaction(
              'rw',
              database.activities,
              database.hash,
              async () => {
                if (!active) return;
                const activities = await database.activities.toArray();
                const removed = activities
                  .filter(({ uuid }) => !ids.has(uuid))
                  .map(({ uuid }) => uuid);
                if (removed.length)
                  await database.activities.bulkDelete(removed);
                if (newActivities.length)
                  await database.activities.bulkPut(newActivities);
                await database.hash.put({
                  key: 'activities',
                  updated_at: Date.now(),
                });
              }
            );
            failures = 0;
          } catch (error) {
            if (!active) return;
            console.error('Failed to refresh library activities', error);
            pending = true;
            const delay = Math.min(1000 * 2 ** Math.min(failures++, 5), 30000);
            timer = setTimeout(() => {
              timer = null;
              void run();
            }, delay);
            // Stay refreshing during backoff so useLive cannot immediately retry.
            break;
          }
        }
      } finally {
        running = false;
        if (active) setRefreshing(timer !== null);
      }
    }

    requestRefresh.current = () => {
      pending = true;
      if (running || timer !== null) return;
      return run();
    };
    return () => {
      active = false;
      pending = false;
      clearTimeout(timer);
      requestRefresh.current = null;
    };
  }, [client, database]);

  return { refresh, isRefreshing };
}
